import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { AgentStrategy, AgentGoal, StrategyState, CompanyCeoStrategy } from '../../services/company-api.js';

@customElement('cp-company-strategy-view')
export class CpCompanyStrategyView extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) strategies: CompanyCeoStrategy[] = [];

  @state() private _expandedIds: Set<number> = new Set();
  @state() private _contentExpandedIds: Set<number> = new Set();

  private _stateLabel(state: StrategyState): string {
    const labels: Record<StrategyState, string> = {
      goal_received: 'Ziel erhalten',
      clarifying: 'Klaerung',
      ready: 'Bereit',
      strategizing: 'Strategieentwicklung',
      strategy_review: 'Review',
      executing: 'In Ausfuehrung',
      blocked: 'Blockiert',
      reporting: 'Reporting',
      completed: 'Abgeschlossen',
      failed: 'Fehlgeschlagen',
    };
    return labels[state] ?? state;
  }

  private _inlineMarkdown(text: string): string {
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
  }

  private _renderMarkdownLines(lines: string[]) {
    return lines.map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return nothing;
      if (trimmed.startsWith('# ')) {
        return html`<p class="cp-strategy__content-heading">${unsafeHTML(this._inlineMarkdown(trimmed.slice(2)))}</p>`;
      }
      if (trimmed.startsWith('## ')) {
        return html`<p class="cp-strategy__content-subheading">${unsafeHTML(this._inlineMarkdown(trimmed.slice(3)))}</p>`;
      }
      if (trimmed.startsWith('### ')) {
        return html`<p class="cp-strategy__content-subheading">${unsafeHTML(this._inlineMarkdown(trimmed.slice(4)))}</p>`;
      }
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        return html`<p class="cp-strategy__content-list-item">${unsafeHTML(this._inlineMarkdown(trimmed.slice(2)))}</p>`;
      }
      if (trimmed === '---') return nothing;
      return html`<p class="cp-strategy__content-text">${unsafeHTML(this._inlineMarkdown(trimmed))}</p>`;
    });
  }

  private _renderContent(content: string, strategyId: number) {
    const lines = content.split('\n');
    const PREVIEW_LINES = 12;
    const isLong = lines.length > PREVIEW_LINES;
    const isContentExpanded = this._contentExpandedIds.has(strategyId);
    const visibleLines = isContentExpanded ? lines : lines.slice(0, PREVIEW_LINES);

    return html`
      <div class="cp-strategy__content">
        ${this._renderMarkdownLines(visibleLines)}
        ${isLong && !isContentExpanded ? html`
          <button class="cp-strategy__expand-btn" @click=${(e: Event) => { e.stopPropagation(); this._toggleContentExpand(strategyId); }}>
            Strategie komplett anzeigen
          </button>
        ` : nothing}
        ${isLong && isContentExpanded ? html`
          <button class="cp-strategy__expand-btn" @click=${(e: Event) => { e.stopPropagation(); this._toggleContentExpand(strategyId); }}>
            Weniger anzeigen
          </button>
        ` : nothing}
      </div>
    `;
  }

  private _toggleContentExpand(strategyId: number): void {
    const next = new Set(this._contentExpandedIds);
    if (next.has(strategyId)) {
      next.delete(strategyId);
    } else {
      next.add(strategyId);
    }
    this._contentExpandedIds = next;
  }

  private _toggleCard(strategyId: number): void {
    const next = new Set(this._expandedIds);
    if (next.has(strategyId)) {
      next.delete(strategyId);
    } else {
      next.add(strategyId);
    }
    this._expandedIds = next;
  }

  private _renderStrategyCard(item: CompanyCeoStrategy) {
    const { strategy, goal } = item;
    if (!strategy || !goal) return nothing;

    const isExpanded = this._expandedIds.has(strategy.id);

    return html`
      <div class="cp-strategy__card${isExpanded ? ' cp-strategy__card--expanded' : ''}">
        <div class="cp-strategy__card-header" @click=${() => this._toggleCard(strategy.id)}>
          <div class="cp-strategy__goal-area">
            <p class="cp-strategy__goal-title">${goal.title}</p>
            ${!isExpanded && goal.description ? html`
              <p class="cp-strategy__goal-desc">${goal.description}</p>
            ` : nothing}
          </div>
          <span class="cp-strategy__state-badge cp-strategy__state-badge--${strategy.state}">
            ${this._stateLabel(strategy.state)}
          </span>
        </div>

        ${isExpanded ? html`
          ${goal.description ? html`
            <p class="cp-strategy__goal-desc" style="margin-top:var(--spacing-xs)">${goal.description}</p>
          ` : nothing}

          ${strategy.strategy_content ? html`
            <div class="cp-strategy__content-box">
              ${this._renderContent(strategy.strategy_content, strategy.id)}
            </div>
          ` : nothing}

          ${strategy.execution_report ? html`
            <div class="cp-strategy__report-box">
              <p class="cp-strategy__report-label">Ergebnis-Report</p>
              <div class="cp-strategy__content">
                ${this._renderMarkdownLines(strategy.execution_report.split('\n'))}
              </div>
            </div>
          ` : nothing}

          <p class="cp-strategy__date">
            Aktualisiert: ${new Date(strategy.updated_at).toLocaleDateString('de-DE')}
          </p>
        ` : nothing}
      </div>
    `;
  }

  override render() {
    if (!this.strategies || this.strategies.length === 0) {
      return html`
        <div class="cp-strategy__empty">
          <p class="cp-strategy__empty-text">Noch keine Strategie definiert</p>
          <p class="cp-strategy__empty-hint">Starte einen Chat mit dem CEO, um eine Strategie zu entwickeln.</p>
        </div>
      `;
    }

    return html`
      <div class="cp-strategy__list">
        ${this.strategies.map((item) => this._renderStrategyCard(item))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-strategy-view': CpCompanyStrategyView;
  }
}
